import {
  ComponentProps,
  MouseEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { captureException } from "@sentry/browser";
import queryString from "query-string";
import { useLocation } from "react-router";
import { FEATURE_FLAG_NAME } from "common/constants";
import { MapsForAccountQuery } from "../../generated/graphql";
import { useCanGeolocate } from "../../hooks/useCanGeolocate";
import { useIsScreenSize } from "../../hooks/useWindowSize";
import AccountActivityFeed from "../ActivityFeed/AccountActivityFeed";
import AddressPanel from "../AddressPanel";
import { AuthContext } from "../Authorization/AuthContext";
import { VisuallyHidden } from "../Common/VisuallyHidden";
import ObjectInformationPanel from "../ObjectInformationPanel";
import { OldOrNewSearch } from "../Search";
import { SearchContext } from "../Search/SearchContext";
import { ExitButton } from "./ExitButton";
import GeolocateBanner from "./GeolocateBanner";
import { ClickObjectMenuLayerConfig } from "./LayeredMap/clickObjectMenuLayer";
import { CustomMapLayerConfig } from "./LayeredMap/customMapLayer";
import { LayeredMap } from "./LayeredMap/LayeredMap";
import { ParcelLayerConfig } from "./LayeredMap/parcelLayer";
import { PropertyLayerConfig } from "./LayeredMap/propertyLayer";
import { SavedViewsLayerConfig } from "./LayeredMap/savedViewsLayer";
import { LayerConfigs } from "./LayeredMap/types";
import { LayerContext } from "./layers";
import LayersPanel from "./LayersPanel";
import { Selected } from "./Map";
import { CreatePropertyTool } from "./MapTools/CreatePropertyTool";
import { GeolocationTool } from "./MapTools/GeolocationTool";
import { LegendControlTool } from "./MapTools/LegendControl";
import { MeasureTool } from "./MapTools/MeasureTool/MeasureTool";
import { UserDeviceLocationLegendControl } from "./MapTools/UserDeviceLocation/UserDeviceLocationLegendControl";
import { DeviceLocationsConfig } from "./MapTools/UserDeviceLocation/useUserDeviceLocations";
import { ZoomControl } from "./MapTools/ZoomControl";
import { SavedViewBanner } from "./SavedViewBanner";
import { MapViewportContext } from "./utils/MapViewportContext";

const useQueryParams = (
  account: NonNullable<MapsForAccountQuery["account"]>,
) => {
  const location = useLocation();
  const params = queryString.parse(location.search);
  const longitude = parseFloat(params.lng as string);
  const latitude = parseFloat(params.lat as string);
  let selectedFirmId = params.firmId as Maybe<string>;
  if (!account.firms.some(accountFirm => accountFirm.id === selectedFirmId)) {
    selectedFirmId = account.firms.find(f => f.isDefault)?.id || null;
  }

  return {
    latitude,
    longitude,
    selectedFirmId,
  };
};

interface FullpageMapProps {
  account: NonNullable<MapsForAccountQuery["account"]>;
  parcelConfig: ParcelLayerConfig;
  propertyConfig: PropertyLayerConfig;
  savedViewsConfig: SavedViewsLayerConfig;
  deviceLocationsConfig?: DeviceLocationsConfig;
  customMapsConfig: CustomMapLayerConfig;
  clickObjectMenuConfig: ClickObjectMenuLayerConfig;
  selected?: Selected;
  onAddressPanelClose?: () => void;
  handleExitClick?: () => void;
}

const FullPageMap = ({
  account,
  parcelConfig,
  propertyConfig,
  savedViewsConfig,
  deviceLocationsConfig,
  customMapsConfig,
  clickObjectMenuConfig,
  selected,
  onAddressPanelClose,
  handleExitClick,
}: FullpageMapProps) => {
  const { isFeatureEnabled, isPublic } = useContext(AuthContext);
  const isMeasureToolEnabled = isFeatureEnabled(
    FEATURE_FLAG_NAME.MEASURE_TOOL_WEB,
  );

  const {
    toggleLayer,
    visibleFIRM,
    visibleRaster: getVisibleRaster,
    visibleSavedViews: getVisibleSavedViews,
    visibleLayerIdsForGroup,
    measureToolDispatch,
    measureToolState,
    approximateBfeToolState,
    approximateBfeToolDispatch,
    markerPoint,
  } = useContext(LayerContext);
  const { searchQueryState } = useContext(SearchContext);

  const [layersPanelVisible, toggleLayersPanel] = useState(false);
  const [showGeolocateBanner, setShowGeolocateBanner] = useState(false);
  const [savedViewIdToShowBannerFor, setSavedViewIdToShowBannerFor] = useState(
    savedViewsConfig.value?.[0]?.id,
  );
  const visibleSavedViewIds = visibleLayerIdsForGroup("savedViews");
  useEffect(() => {
    if (
      visibleSavedViewIds.length === 1 &&
      savedViewIdToShowBannerFor === visibleSavedViewIds[0]
    ) {
      return;
    }
    setSavedViewIdToShowBannerFor(undefined);
  }, [JSON.stringify(visibleSavedViewIds)]);

  const visibleRaster = getVisibleRaster();

  const { selectedFirmId } = useQueryParams(account);
  const isMobile = useIsScreenSize("mobile");

  if (
    measureToolState.measureToolMode === "distance" ||
    measureToolState.measureToolMode === "raster" ||
    approximateBfeToolState?.mode === "on"
  ) {
    parcelConfig = {
      ...parcelConfig,
      interactive: { hover: false, click: false },
    };
    propertyConfig = {
      ...propertyConfig,
      interactive: { hover: false, click: false },
    };
    savedViewsConfig = {
      ...savedViewsConfig,
      interactive: { hover: false, click: false },
    };
    customMapsConfig = {
      ...customMapsConfig,
      interactive: { hover: false },
    };
  }

  const layers: LayerConfigs = {
    parcels: parcelConfig,
    firms: {
      firms: account.firms,
      onClick: crossSection => {
        approximateBfeToolDispatch?.({
          type: "toggleCrossSection",
          data: { crossSection },
        });
      },
      interactive: {
        click:
          approximateBfeToolState?.mode === "on" &&
          !approximateBfeToolState.disableMapInteractions,
        hover: approximateBfeToolState?.mode === "on",
      },
    },
    measure: {
      onClick: coordinates => {
        measureToolDispatch({
          type: "setCoordinates",
          data: { coordinates },
        });
      },
      interactive: {
        click:
          (measureToolState.measureToolMode === "distance" ||
            measureToolState.measureToolMode === "raster") &&
          !measureToolState.loading,
      },
    },
    raster: {
      rasters: account.rasters,
    },
    accountBoundary: {},
    marker: {
      value:
        searchQueryState.searchPoint ? searchQueryState.searchPoint
        : selected ? markerPoint
        : null,
    },
    properties: propertyConfig,
    contour: {},
    cbrs: {},
    clickObjectMenu: clickObjectMenuConfig,
    customMaps: customMapsConfig,
    savedViews: savedViewsConfig,
    savedViewsCluster: {
      value: savedViewsConfig.value,
      onClick: async (lngLat: [number, number]) => {
        await zoomTo({
          latitude: lngLat[1],
          longitude: lngLat[0],
          zoom: syncedViewport.zoom + 1,
        });
      },
      interactive: {
        click: !!savedViewsConfig.interactive?.click,
        hover: false,
      },
    },
    deviceLocations: deviceLocationsConfig ?? {},
    searchResults: {
      interactive: {
        click: true,
        hover: true,
      },
    },
  };

  const infoPanelOpen = !!selected || searchQueryState.searchPanelOpen;

  const { syncedViewport, zoomTo, setInfoPanelOpen } =
    useContext(MapViewportContext);

  useEffect(() => {
    setInfoPanelOpen(infoPanelOpen);
  }, [infoPanelOpen, setInfoPanelOpen]);

  // Create a ref to keep track of previous zoom level. This is so we can
  // compare it against the current zoom level and only announce it to screen
  // readers if the zoom level has changed.
  const prevZoomRef = useRef<number>(syncedViewport.zoom);
  const zoomAnnouncementRef = useRef<HTMLDivElement>(null);

  // Set zoom level in aria-live region when it changes
  useEffect(() => {
    const prevZoom = prevZoomRef.current;
    const currentZoom = syncedViewport.zoom;

    if (prevZoom !== currentZoom && zoomAnnouncementRef.current) {
      // Round down, since Mapbox's zoom level starts at 0
      const roundedZoom = Math.floor(currentZoom);
      zoomAnnouncementRef.current.textContent = `Zoom level ${roundedZoom}`;
    }

    prevZoomRef.current = currentZoom;
  }, [syncedViewport.zoom]);

  const handleLayersToggle = useCallback((evt?: MouseEvent | KeyboardEvent) => {
    evt?.preventDefault();
    toggleLayersPanel(prev => !prev);
  }, []);

  const visibleBaseMapId = visibleLayerIdsForGroup("baseMaps")[0];
  const baseMapStyle = useMemo(
    () =>
      account.baseMaps.find(map => map.id === visibleBaseMapId)?.mapboxStyle,
    [account.baseMaps, visibleBaseMapId],
  );

  const geolocateClicked = async () => {
    let geolocationPermissions = { state: "denied" };
    const excludedErrorMessages = [
      "Permissions::query does not support this API",
      "The operation is not supported.",
    ];

    // the availability of the geolocation API is super spotty
    // for mobile iOS in particular for some reason - perhaps
    // specific phone settings? - and so we just blanket this
    // with some pokemon exception handling
    try {
      geolocationPermissions = await navigator.permissions.query({
        name: "geolocation",
      });
    } catch (error) {
      if (
        error instanceof Error &&
        !excludedErrorMessages.some(message => error.message.includes(message))
      ) {
        captureException(error);
      }
    }

    if (geolocationPermissions.state === "denied") {
      setShowGeolocateBanner(true);
    }
  };

  useEffect(() => {
    if (selectedFirmId) {
      toggleLayer({ group: "firms", id: selectedFirmId, isVisible: true });
    }
  }, [selectedFirmId]);

  // This check is necessary because in some very particular and mysterious
  // circumstances, the navigator.permissions.query Promise inside of
  // GeolocateControl rejects and is unhandled, which raises an alert in
  // Sentry. To prevent this, safely check if the permission query *will* fail
  // and only show the control if it does not.
  const [canGeolocate] = useCanGeolocate();

  const activeFloodLayerName = visibleFIRM()?.name ?? visibleRaster?.layerName;

  const hideNavigationControls =
    (measureToolState.measureToolMode === "raster" ||
      measureToolState.measureToolMode === "distance") &&
    isMobile;

  const visibleSavedViews = getVisibleSavedViews();

  return (
    <>
      {/* Used to announce Map zoom level changes to screen readers */}
      <VisuallyHidden
        ref={zoomAnnouncementRef}
        aria-live="polite"
        aria-atomic="true"
      />

      <LayeredMap
        height="100%"
        width="100%"
        baseMapStyle={baseMapStyle}
        account={account}
        layers={layers}
        topLeftMapOverlayItems={[
          <OldOrNewSearch backgroundPanel />,
          !isPublic && !searchQueryState.searchPanelOpen && !infoPanelOpen ?
            <AccountActivityFeed />
          : null,
        ].filter(Boolean)}
        rightPanelOverlayItems={
          layersPanelVisible ?
            [
              <LayersPanel
                close={handleLayersToggle}
                firms={account.firms}
                rasters={account.rasters}
                baseMaps={account.baseMaps}
                accountDocumentTypes={account.accountDocumentTypes}
                accountPropertyWarningDefinitions={
                  account.accountPropertyWarningDefinitions
                }
                savedViews={account.savedViews}
                disableRasterToggle={measureToolState.loading}
                disableFirmToggle={approximateBfeToolState?.mode !== "off"}
              />,
            ]
          : []
        }
        topRightMapOverlayItems={
          handleExitClick ?
            [<ExitButton handleExitClick={handleExitClick} />]
          : undefined
        }
        bottomRightMapOverlayItems={
          !hideNavigationControls && !layersPanelVisible ?
            [
              deviceLocationsConfig ?
                <UserDeviceLocationLegendControl
                  deviceLocationsConfig={deviceLocationsConfig}
                />
              : null,
              canGeolocate ?
                <GeolocationTool geolocateClicked={geolocateClicked} />
              : null,
              isMeasureToolEnabled ? <MeasureTool /> : null,
              !isPublic ?
                <CreatePropertyTool viewport={syncedViewport} />
              : null,
              <ZoomControl />,
              <LegendControlTool
                activeFloodLayerName={activeFloodLayerName}
                handleLayersToggle={handleLayersToggle}
              />,
            ].filter(Boolean)
          : undefined
        }
        leftPanelOverlayItems={
          selected && onAddressPanelClose ?
            [
              selected.type === "address" ?
                <AddressPanel {...selected} onClose={onAddressPanelClose} />
              : <ObjectInformationPanel
                  {...selected}
                  onClose={onAddressPanelClose}
                />,
            ]
          : undefined
        }
      />
      {showGeolocateBanner && !layersPanelVisible && (
        <GeolocateBanner
          onClose={() => {
            setShowGeolocateBanner(false);
          }}
          infoPanelOpen={infoPanelOpen}
        />
      )}

      <SavedViewBanner
        visibleSavedView={visibleSavedViews?.find(
          savedView => savedView.id === savedViewIdToShowBannerFor,
        )}
        zoom={syncedViewport.zoom}
        infoPanelOpen={infoPanelOpen}
      />
      {measureToolState.render?.()}
      {approximateBfeToolState?.render?.()}
    </>
  );
};

export type FullPageMapContainerProps = Omit<
  ComponentProps<typeof FullPageMap>,
  "clickObjectMenuConfig"
>;

export default FullPageMap;
